import React from 'react';
import { styled } from '@linaria/react';
import chroma from 'chroma-js';

import { clamp, normalize } from '@/utils';
import useMousePositionWithinElement from '@/hooks/use-mouse-position-within-element';
import useWindowDimensions from '@/hooks/use-window-dimensions';

import GradientColorBreakdown from './GradientColorBreakdown';

const INDICATOR_SQUARE_SIZE = 100;
const CONNECTING_PIPE_LENGTH = 35;
const BAR_BORDER_THICKNESS = 3;

function GradientSlider({ from, to, colorMode = 'rgb' }) {
  const barRef = React.useRef();

  const [sliderPercentage, setSliderPercentage] = React.useState(0);
  const [isSliding, setIsSliding] = React.useState(false);

  const windowDimensions = useWindowDimensions();

  const [mousePos, boundingBox] = useMousePositionWithinElement(
    barRef,
    {
      buffer: INDICATOR_SQUARE_SIZE,
    }
  );

  React.useEffect(() => {
    if (!isSliding || !mousePos || !boundingBox) {
      return;
    }

    const percentageThrough = (mousePos.x / boundingBox.width) * 100;

    setSliderPercentage(clamp(percentageThrough, 0, 100));
  }, [isSliding, mousePos, boundingBox]);

  React.useEffect(() => {
    function handleMouseUp(ev) {
      setIsSliding(false);
    }

    if (isSliding) {
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchend', handleMouseUp);
      document.addEventListener('mouseleave', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchend', handleMouseUp);
      document.removeEventListener('mouseleave', handleMouseUp);
    };
  }, [isSliding]);

  function startDragging(ev) {
    ev.preventDefault();

    setIsSliding(true);
  }

  function handleKeyDown(ev, index) {
    if (!ev.key.match(/^Arrow/)) {
      return;
    }

    ev.preventDefault();

    switch (ev.key) {
      case 'ArrowLeft': {
        setSliderPercentage((p) => clamp(p - 5, 0, 100));
        return;
      }
      case 'ArrowRight': {
        setSliderPercentage((p) => clamp(p + 5, 0, 100));
        return;
      }
    }
  }

  const gradientColors = chroma
    .scale([from, to])
    .mode(colorMode)
    .colors(20);

  const gradient = `to right, ${gradientColors.join(', ')}`;

  let indicatorOffset = boundingBox
    ? normalize(sliderPercentage, 0, 100, 0, boundingBox.width)
    : 0;

  if (windowDimensions?.width < 800 && boundingBox) {
    indicatorOffset = clamp(
      indicatorOffset,
      INDICATOR_SQUARE_SIZE / 2,
      boundingBox.width -
        INDICATOR_SQUARE_SIZE / 2 +
        BAR_BORDER_THICKNESS * 2
    );
  }

  const pipeOffset = boundingBox
    ? normalize(
        sliderPercentage,
        0,
        100,
        BAR_BORDER_THICKNESS * 2,
        boundingBox.width
      )
    : BAR_BORDER_THICKNESS * 2;

  const currentColor = chroma.mix(
    from,
    to,
    sliderPercentage / 100,
    colorMode
  );

  const flipPercentage = windowDimensions?.width < 800 ? 60 : 85;

  const breakdownPosition =
    sliderPercentage > flipPercentage
      ? {
          left: 0,
          transform: 'translateX(calc(-100% - 16px))',
        }
      : {
          left: 'calc(100% + 16px)',
        };

  return (
    <Wrapper>
      {windowDimensions && (
        <>
          <IndicatorWrapper style={{ left: indicatorOffset }}>
            <ColorPreview
              style={{ backgroundColor: currentColor.hex() }}
              onKeyDown={handleKeyDown}
              onMouseDown={startDragging}
              onTouchStart={startDragging}
            />
            <BreakdownWrapper style={breakdownPosition}>
              <GradientColorBreakdown
                color={currentColor}
                colorMode={colorMode}
              />
            </BreakdownWrapper>
          </IndicatorWrapper>
          <IndicatorPipe style={{ left: pipeOffset }} />
        </>
      )}
      <BarWrapper>
        <Bar
          ref={barRef}
          style={{ backgroundImage: `linear-gradient(${gradient})` }}
          onMouseDown={startDragging}
          onTouchStart={startDragging}
        />
      </BarWrapper>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  padding-top: ${INDICATOR_SQUARE_SIZE + CONNECTING_PIPE_LENGTH}px;
`;

const BarWrapper = styled.div`
  border: ${BAR_BORDER_THICKNESS}px solid var(--color-text);
  height: 50px;
  border-radius: 6px;
  overflow: hidden;
  /* TODO shadow? */
`;

const IndicatorPipe = styled.div`
  position: absolute;
  height: ${CONNECTING_PIPE_LENGTH + BAR_BORDER_THICKNESS}px;
  top: ${INDICATOR_SQUARE_SIZE}px;
  width: 2px;
  transform: translateX(-50%);
  background: var(--color-text);
`;

const IndicatorWrapper = styled.div`
  position: absolute;
  top: 0;
  transform: translateX(-50%);
`;

const ColorPreview = styled.button`
  width: ${INDICATOR_SQUARE_SIZE}px;
  height: ${INDICATOR_SQUARE_SIZE}px;
  border: ${BAR_BORDER_THICKNESS}px solid var(--color-text);
  border-radius: 6px;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }

  &:focus-visible {
    outline-offset: 4px;
  }
`;

const Bar = styled.div`
  width: 100%;
  height: 100%;
  cursor: pointer;
  touch-action: none;
`;

const BreakdownWrapper = styled.div`
  position: absolute;
  top: 0;
  height: 100%;
`;

export default GradientSlider;
